<template>
  <IntegrationWorkflowWrapper
    v-model:is-enabled="trayState.partnerFieldMappingsPush"
    title="Map Partner Data to CRM"
    :description="description"
    data-testid="partner-field-mappings-workflow"
    :status="getStatusByWorkflowName(workflowName)"
    :has-learn-more="true"
    v-slot="{ isSlotDisabled }"
    :suppress-slot-error="true"
  >
    <div class="flex flex-col gap-16 mt-16 mb-16">
      <div class="flex items-center">
        <div class="w-full">
          <TextWithTooltip
            text="Partner-shared account fields"
            text-classes="text-neutral-text-weak"
            tooltip-text="These are the account fields your partners are sharing with you."
          />
        </div>

        <FontAwesomeIcon :icon="['fak', 'arrow-right']" class="invisible" />

        <div class="flex items-center w-full gap-8">
          <TextWithTooltip
            text="Your CRM fields"
            text-classes="text-neutral-text-weak"
            tooltip-text="These are your CRM fields in which partner-shared fields will be pushed."
          />
          <BittsTooltip>
            <template #title>Sync fields</template>
            <BittsButton
              @click="refresh"
              type="neutral"
              size="small"
              variant="ghost"
              :center-icon="['fak', 'refresh']"
              :center-icon-classes="trayState.isSyncing ? 'animate-spin' : ''"
              :aria-disabled="
                disabled || isSlotDisabled || !integrationCRMSourceId
              "
              data-testid="refresh-fields"
              aria-label="Sync fields"
            />
          </BittsTooltip>
        </div>
        <div class="w-40" />
      </div>
      <PartnerFieldMapping
        v-for="(mapping, idx) in mappings"
        :key="mapping.id"
        :partner-fields="sharedPartnerFields"
        :crm-fields="getAvailableSourceFields(mapping.source_field_id)"
        :mapping="mapping"
        :index="idx"
        :disabled="disabled || isSlotDisabled"
        @changed:mapping="updateMapping"
        @deleted:mapping="deleteMapping"
      />
      <div class="flex justify-between items-center">
        <BittsButton
          text="Add field to map"
          variant="outline"
          size="small"
          :left-icon="['fak', 'add']"
          class="max-w-max"
          :disabled="disabled || isSlotDisabled"
          @click="
            () => {
              mappings.push({
                id: `mapping-${mappings.length}`,
                source_field_id: null,
                partner_organization_uuid: null,
                partner_source_field_id: null,
                empty: true,
              });
            }
          "
        />
      </div>
    </div>
  </IntegrationWorkflowWrapper>
</template>

<script setup lang="ts">
import { BittsButton, BittsTooltip } from '@crossbeam/bitts';

import { keyBy } from 'lodash';
import { storeToRefs } from 'pinia';
import { computed, inject } from 'vue';

import { crossbeamApi } from '@/api';
import { ProvidedTrayState } from '@/injectionKeys/integration_profiles';
import {
  useFeedsStore,
  useFlashesStore,
  usePartnerCloudStore,
  useSourcesStore,
} from '@/stores';
import { WorkflowsWithStatus } from '@/types/integrations';
import { Partner } from '@/types/partners';

import TextWithTooltip from '../performance-dashboard/TextWithTooltip.vue';

import IntegrationWorkflowWrapper from './IntegrationWorkflowWrapper.vue';
import PartnerFieldMapping, { Mapping } from './PartnerFieldMapping.vue';

export interface PartnerOption extends Partner {
  label: string;
  value: number;
}

const { addSuccessFlash, addErrorFlash } = useFlashesStore();
const { getStatusByWorkflowName } = usePartnerCloudStore();
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const trayState = inject(ProvidedTrayState)!;

const description = computed(
  () =>
    `Push partner-shared account fields directly into your CRM. Enrich your ${trayState.friendlyName} workflows, reports, and processes with unique data from your partners’ CRMs.`,
);

const workflowName = computed<WorkflowsWithStatus | null>(() => {
  switch (trayState.profileType) {
    case 'salesforce':
      return 'SCH.Partner fields to CSV [SFDC Partner]';
    case 'hubspot':
      return 'Sch. HubSpot Partner Fields Push';
    default:
      return null;
  }
});

const sharedPartnerFields = computed(
  () => trayState?.sharedPartnerFields ?? [],
);

const mappings = computed(() => trayState?.partnerFieldMappings);

const disabled = computed(
  () =>
    !trayState.partnerFieldMappingsPush ||
    !trayState.isEnabled ||
    trayState.isSyncing,
);

const sourcesStore = useSourcesStore();
const feedsStore = useFeedsStore();

async function refresh() {
  trayState.isSyncing = true;

  const { response } = await crossbeamApi.POST(
    '/v0.3/sources/{id}/fields/refresh',
    {
      params: {
        path: {
          id: integrationCRMSourceId.value as number, // button triggering refresh is disabled if no IntegrationCRMSourceId, so it's safe to cast as number
        },
      },
    },
  );

  if (response.status === 204) {
    await sourcesStore.refreshSourcesStore();
    addSuccessFlash({
      message: 'Your CRM fields have been successfully refreshed!',
    });
  } else {
    addErrorFlash({
      message: 'Something went wrong, reach out to our support team for help',
    });
  }

  trayState.isSyncing = false;
}

const { crmFeeds } = storeToRefs(feedsStore);

const integrationCRMFeedIds = computed(() => {
  const profileType = trayState.profileType;
  const feedType = profileType === 'hubspot' ? 'hubspot_v3' : profileType;
  return new Set(
    crmFeeds.value
      .filter((feed) => feed.integration.type === feedType)
      .map((feed) => feed.id),
  );
});

/* An org can have several feeds of the same CRM type, and only one of them
owns the account source. Match on the source rather than picking a feed first. */
const integrationCRMAccountSource = computed(() =>
  sourcesStore
    .getSourcesByMdmType('account')
    ?.find(
      (source) =>
        !!source.feed_id && integrationCRMFeedIds.value.has(source.feed_id),
    ),
);

const integrationCRMSourceId = computed(
  () => integrationCRMAccountSource.value?.id,
);

const integrationCRMSources = computed(() => {
  const accountSource = integrationCRMAccountSource.value;

  if (!accountSource) return [];

  return accountSource?.fields
    .filter(
      (f) =>
        f.original_data_type &&
        ['string', 'textarea'].includes(f.original_data_type) &&
        f.syncing_disabled_reason !== 'has_disappeared',
    )
    .map((f) => ({
      label: f.nickname as string,
      value: f.id as number,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
});

const mappedSourceFieldIds = computed(
  () =>
    new Set(
      mappings.value
        .filter((m) => m.source_field_id !== null)
        .map((m) => m.source_field_id),
    ),
);

const partnerSourceFieldsById = computed(() =>
  keyBy(sharedPartnerFields.value, 'value'),
);

const getAvailableSourceFields = (currentId: number | null) =>
  integrationCRMSources.value.filter(
    (field) =>
      field.value === currentId || !mappedSourceFieldIds.value.has(field.value),
  );

function deleteMapping(index: number) {
  if (mappings.value.length === 1) {
    mappings.value[0] = {
      id: 'foo',
      source_field_id: null,
      partner_organization_uuid: null,
      partner_source_field_id: null,
      empty: true,
    };
    return;
  }
  const mappingToDelete = mappings.value[index];
  if (!mappingToDelete) return;
  mappings.value.splice(index, 1);
}

function updateMapping(payload: { mapping: Mapping; index: number }) {
  const { mapping, index } = payload;
  const existingMapping = mappings.value[index];
  const partnerOrgUUID = mapping.partner_source_field_id
    ? (partnerSourceFieldsById.value[mapping.partner_source_field_id]?.uuid ??
      null)
    : null;
  if (!existingMapping) return;
  mappings.value[index] = {
    ...mapping,
    partner_organization_uuid: partnerOrgUUID,
  };
}
</script>
